import { useMemo, type MouseEvent } from "react";
import { Box, Paper, Typography, IconButton, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import type { SidewallSelectorProps } from "../types";
import { SidewallSvgThumbnail } from "./SidewallSvgThumbnail";

export const SidewallSelector = ({
  sidewalls,
  selectedSidewallId,
  onSelectSidewall,
  onSetOnline,
  onSetOffline,
}: SidewallSelectorProps) => {
  // Group sidewalls by online/offline status
  const { onlineSidewalls, offlineSidewalls } = useMemo(() => {
    const online = sidewalls.filter((s) => s.isOnline);
    const offline = sidewalls.filter((s) => !s.isOnline);
    return { onlineSidewalls: online, offlineSidewalls: offline };
  }, [sidewalls]);

  const handlePowerClick = async (
    event: MouseEvent<HTMLButtonElement>,
    sidewallId: number,
    isOnline: boolean,
  ) => {
    event.stopPropagation();
    if (isOnline) {
      await onSetOffline(sidewallId);
    } else {
      await onSetOnline(sidewallId);
    }
  };

  const renderSidewallCard = (sidewall: (typeof sidewalls)[0]) => {
    const isSelected = selectedSidewallId === sidewall.sidewallId;

    return (
      <Paper
        key={sidewall.sidewallId}
        onClick={() => onSelectSidewall(sidewall.sidewallId)}
        elevation={isSelected ? 3 : 1}
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: theme.spacing(1, 2),
          cursor: "pointer",
          minWidth: 130,
          transition: "all 0.2s ease",
          border: isSelected
            ? `2px solid ${theme.palette.primary.main}`
            : "2px solid transparent",
          backgroundColor: isSelected
            ? theme.palette.action.selected
            : "transparent",
          overflow: "visible",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
        })}
      >
        <Box sx={{ mb: 0.25 }}>
          <SidewallSvgThumbnail
            sidewallData={sidewall}
            size={90}
            showNumbers={false}
          />
        </Box>

        <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, mb: 0 }}>
          {sidewall.sidewallName}
        </Typography>

        <Tooltip title={sidewall.isOnline ? "Set Offline" : "Set Online"}>
          <IconButton
            onClick={(e) =>
              handlePowerClick(e, sidewall.sidewallId, sidewall.isOnline)
            }
            sx={(theme) => ({
              padding: theme.spacing(0.5),
              marginTop: theme.spacing(0.5),
              color: sidewall.isOnline
                ? theme.palette.success.main
                : theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: theme.palette.success.main,
              },
            })}
          >
            <PowerSettingsNewIcon />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        borderBottom: `1px solid ${theme.palette.divider}`,
        overflowX: "auto",
      })}
    >
      {/* Online Sidewalls Section - Left */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            py: 0.5,
            color: (theme) => theme.palette.success.dark,
          }}
        >
          <CheckCircleIcon fontSize="small" />
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Online
          </Typography>
        </Box>
        <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(1) })}>
          {onlineSidewalls.map(renderSidewallCard)}
        </Box>
      </Box>

      {/* Offline Sidewalls Section - Right */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            py: 0.5,
            color: "text.secondary",
          }}
        >
          <CancelIcon fontSize="small" />
          <Typography
            sx={{
              fontSize: "0.875rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Offline
          </Typography>
        </Box>
        <Box sx={(theme) => ({ display: "flex", gap: theme.spacing(1) })}>
          {offlineSidewalls.map(renderSidewallCard)}
        </Box>
      </Box>
    </Box>
  );
};
